import React, { Suspense } from "react"
import { useTranslations } from "next-intl"

import { ReducedProductData } from "@/lib/storefront/product/product"
import type { MarketChannel } from "@/lib/market/market-channel"
import FallbackCard from "@/components/common/FallbackCard"
import ProductImpressionsTrackingClient from "@/components/search-and-category/ProductImpressionsTrackingClient"
import SearchResultArticleView from "@/components/search-and-category/SearchResultArticleView"
import SearchResultContainerGridList from "@/components/search-and-category/SearchResultContainerGridList"
import SearchResultPageSizeClient from "@/components/search-and-category/SearchResultPageSizeClient"
import SearchResultPaginationClient from "@/components/search-and-category/SearchResultPaginationClient"
import SearchResultSortOrder from "@/components/search-and-category/SearchResultSortOrder"

interface SearchResultGridProps {
  tracking: string
  sbLocale?: string
  channel?: MarketChannel
  isoLocale: string
  products: ReducedProductData[]
  banner?: string
  bannerPosition?: number
  bannerCampaignId?: string
  templateBlok: any
  productsTotal: number
  isB2B: boolean
  allowDebugMode: boolean | undefined
  hideAddToBasket: boolean | undefined
  inStoryblock: boolean | undefined
  categoryPath?: string[]
  trackProductImpressions?: boolean
  viewItemListPageType?: string
  searchTerm?: string
  campaignId?: string
}

const SearchResultContainer = ({
  tracking,
  sbLocale,
  channel,
  isoLocale,
  products,
  banner,
  bannerPosition,
  bannerCampaignId,
  templateBlok,
  productsTotal,
  isB2B,
  allowDebugMode,
  hideAddToBasket,
  inStoryblock,
  ...restProps
}: SearchResultGridProps) => {
  const t = useTranslations("storefront")
  const categoryPath = restProps.categoryPath?.filter(Boolean) ?? []
  const trackProductImpressions = restProps.trackProductImpressions !== false
  const viewItemListPageType = categoryPath.length > 0 ? undefined : restProps.viewItemListPageType

  return products?.length > 0 ? (
    <div className="flex flex-col">
      {trackProductImpressions && (
        <Suspense>
          <ProductImpressionsTrackingClient
            isoLocale={isoLocale}
            sbLocale={sbLocale}
            channel={channel}
            products={products}
            list={"Category Resultslist"}
            categoryPath={categoryPath}
            pageType={viewItemListPageType}
          />
        </Suspense>
      )}

      <div className={"@container"}>
        <div className="mb-2 grid grid-rows-3 @lg:grid-cols-[1fr_1fr_1fr] @lg:grid-rows-1 @lg:gap-x-2">
          <div className={"@container flex flex-row flex-wrap justify-between gap-x-2"}>
            <div className={"flex h-10 items-center font-bold whitespace-nowrap"}>
              {productsTotal} {t("common.items")}
            </div>
            <div className={"flex h-10 w-fit shrink-0 grow-0"}>
              <SearchResultPageSizeClient productsTotal={productsTotal} className={"p-0!"} />
            </div>
          </div>
          <div className={"flex items-start justify-center"}>
            <div className={"flex h-10 items-center"}>
              <SearchResultPaginationClient productsTotal={productsTotal} />
            </div>
          </div>
          <div className={"@container flex flex-row flex-wrap justify-between gap-x-2"}>
            <SearchResultSortOrder className={"w-fit grow-0 p-0!"} />
            <div className={"flex w-auto grow justify-end"}>
              <SearchResultArticleView />
            </div>
          </div>
        </div>
      </div>
      <Suspense fallback={<FallbackCard className={"bg-gray-100"} />}>
        <SearchResultContainerGridList
          banner={banner}
          bannerPosition={bannerPosition}
          bannerCampaignId={bannerCampaignId}
          templateBlok={templateBlok}
          tracking={tracking}
          products={products}
          sbLocale={sbLocale}
          channel={channel}
          isoLocale={isoLocale}
          isB2B={isB2B}
          allowDebugMode={allowDebugMode}
          hideAddToBasket={hideAddToBasket}
          inStoryBlok={inStoryblock}
          categoryPath={categoryPath}
          searchTerm={restProps.searchTerm}
          campaignId={restProps.campaignId}
        />
      </Suspense>
      <div className="mt-6 flex h-8 flex-row justify-center">
        {<SearchResultPaginationClient productsTotal={productsTotal} />}
      </div>
    </div>
  ) : (
    <div>{t("category.products.empty")}</div>
  )
}

export default SearchResultContainer
